import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import {
  TWO_FACTOR_STATUS_KEY,
  useDisableTwoFactor,
  useSetupTwoFactor,
  useStartTwoFactorSetup,
  useTwoFactorStatus,
  type TwoFactorSetup
} from "@/hooks/useTwoFactor";

type View = "status" | "scan" | "disable";

/**
 * Settings → Security card for 2FA (HOTP via an authenticator app). Three
 * views:
 *   - status:  on/off summary + the button to start the relevant flow
 *   - scan:    QR code + manual-entry secret + 6-digit confirm code
 *   - disable: current-password confirm before turning it off
 */
export function TwoFactorCard() {
  const { toast } = useToast();
  const { verifyTwoFactor } = useAuth();
  const qc = useQueryClient();

  const { data: status, isLoading } = useTwoFactorStatus();
  const startSetup = useStartTwoFactorSetup();
  const setup = useSetupTwoFactor();
  const disable = useDisableTwoFactor();

  const [view, setView] = React.useState<View>("status");
  const [setupData, setSetupData] = React.useState<TwoFactorSetup | null>(null);
  const [code, setCode] = React.useState("");
  const [confirming, setConfirming] = React.useState(false);
  const [setupError, setSetupError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const [disablePassword, setDisablePassword] = React.useState("");
  const [disableError, setDisableError] = React.useState<string | null>(null);

  function resetAndClose() {
    setView("status");
    setSetupData(null);
    setCode("");
    setSetupError(null);
    setDisablePassword("");
    setDisableError(null);
  }

  async function beginSetup() {
    setSetupError(null);
    try {
      await startSetup.mutateAsync();
      const data = await setup.mutateAsync();
      setSetupData(data);
      setView("scan");
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Couldn't start 2FA setup. Try again.";
      toast({ title: "Couldn't start setup", description: message, variant: "destructive" });
    }
  }

  async function confirmSetup(e: React.FormEvent) {
    e.preventDefault();
    setSetupError(null);
    if (confirming) return;
    setConfirming(true);
    try {
      await verifyTwoFactor(code.trim());
      await qc.invalidateQueries({ queryKey: TWO_FACTOR_STATUS_KEY });
      toast({ title: "Two-factor authentication is on", description: "You'll be asked for a code each time you sign in." });
      resetAndClose();
    } catch {
      // Covers both "wrong code" and "setup session expired" — the backend
      // can't fully tell these apart from here, and this reads fine either way.
      setSetupError("That code didn't work, or your setup session expired. Try again.");
    } finally {
      setConfirming(false);
    }
  }

  async function confirmDisable(e: React.FormEvent) {
    e.preventDefault();
    setDisableError(null);
    if (disable.isPending) return;
    try {
      const res = await disable.mutateAsync(disablePassword);
      if (!res.success) {
        setDisableError(res.message);
        return;
      }
      toast({ title: "Two-factor authentication is off" });
      resetAndClose();
    } catch (err) {
      setDisableError(err instanceof ApiError ? err.message : "Couldn't turn off 2FA. Try again.");
    }
  }

  async function copySecret() {
    if (!setupData) return;
    try {
      await navigator.clipboard.writeText(setupData.secretKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be unavailable (permissions, non-HTTPS) — the
      // secret is already selectable as plain text, so this is a soft-fail.
    }
  }

  const starting = startSetup.isPending || setup.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          Two-factor authentication
        </CardTitle>
        <CardDescription>
          Require a code from an authenticator app (Google Authenticator, Authy, 1Password…) in
          addition to your password when signing in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking status…
          </div>
        ) : view === "scan" && setupData ? (
          <form onSubmit={confirmSetup} className="space-y-4 sm:max-w-sm">
            <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-muted/30 p-4">
              <img
                src={`data:image/png;base64,${setupData.qrCodeBase64}`}
                alt="Scan this QR code in your authenticator app"
                className="h-44 w-44 rounded-md border border-border bg-white p-2"
              />
              <p className="text-center text-xs text-muted-foreground">
                Scan with your authenticator app. It'll show <span className="font-medium text-foreground">HosterDash</span> as
                the account name.
              </p>
            </div>

            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer font-medium text-foreground">
                Can't scan? Enter the code manually
              </summary>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 select-all break-all rounded-md border border-border bg-muted/50 px-2 py-1.5 text-[11px]">
                  {setupData.secretKey}
                </code>
                <Button type="button" size="icon" variant="outline" onClick={copySecret} title="Copy">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              {copied && <p className="mt-1 text-primary">Copied.</p>}
            </details>

            <div className="space-y-2">
              <Label htmlFor="confirm-otp">6-digit code from the app</Label>
              <Input
                id="confirm-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="text-center text-lg tracking-[0.5em]"
              />
            </div>
            {setupError && <p className="text-sm font-medium text-destructive">{setupError}</p>}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button type="submit" disabled={confirming || code.length < 6}>
                {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
                Enable 2FA
              </Button>
              <Button type="button" variant="ghost" onClick={resetAndClose}>
                Cancel
              </Button>
            </div>
          </form>
        ) : view === "disable" ? (
          <form onSubmit={confirmDisable} className="space-y-4 sm:max-w-sm">
            <div className="space-y-2">
              <Label htmlFor="disable-password">Confirm your password</Label>
              <Input
                id="disable-password"
                type="password"
                autoComplete="current-password"
                required
                value={disablePassword}
                onChange={e => setDisablePassword(e.target.value)}
              />
            </div>
            {disableError && <p className="text-sm font-medium text-destructive">{disableError}</p>}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button type="submit" variant="destructive" disabled={disable.isPending}>
                {disable.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Turn off 2FA
              </Button>
              <Button type="button" variant="ghost" onClick={resetAndClose}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  status?.enabled ? "bg-brand-green/10 text-brand-green" : "bg-muted text-muted-foreground"
                }`}
              >
                {status?.enabled ? <ShieldCheck className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
              </div>
              <div>
                <div className="flex items-center gap-2 font-medium">
                  {status?.enabled ? "Enabled" : "Not enabled"}
                  <Badge variant={status?.enabled ? "success" : "secondary"}>
                    {status?.enabled ? "On" : "Off"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {status?.enabled
                    ? "A code from your authenticator app is required at sign-in."
                    : "Add an extra layer of protection to your account."}
                </p>
              </div>
            </div>
            {status?.enabled ? (
              <Button variant="outline" onClick={() => setView("disable")}>
                <ShieldOff className="h-4 w-4" /> Turn off
              </Button>
            ) : (
              <Button onClick={beginSetup} disabled={starting}>
                {starting && <Loader2 className="h-4 w-4 animate-spin" />}
                Set up 2FA
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
